import { Checkbox, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { SECURITY_ACTIONS } from '../../../../auth/Security';
import type { SecuredActionType } from '../../../../components/ActionType';
import EllipsisText from '../../../../components/EllipsisText';
import ResponsiveActions from '../../../../components/ResponsiveActions';
import TagsPreview from '../../../../components/TagsPreview';
import { SIZES } from '../../../../constants/constants';
import { ENTITY_CONSTANTS } from '../../../../constants/entityConstants';
import { Icons } from '../../../../icons/Icons';
import { H, V } from '../../../../Layout';
import { MARKETPLACE_INNER_ITEM_PADDING } from '../../../constants';
import type { WorkEntryRow } from '../../../types';
import { estimateRoleChipTileWidth } from '../../utils';

const ENTRY_ITEM_HEIGHT_PX = 128;

type WorkEntryItemProps<TSource> = {
    row: WorkEntryRow<TSource>;
    selected: boolean;
    toggleEntry: (id: string) => void;
    showDragHandle?: boolean;
    showCheckbox?: boolean;
    showKeyIcon?: boolean;
    keyEntryTooltip?: ReactNode;
    marketplaceInnerItemPadding?: boolean;
    rowActionsEnabled?: boolean;
    onEdit?: (row: WorkEntryRow<TSource>) => void;
    onRequestDelete?: (row: WorkEntryRow<TSource>) => void;
};

const clampedLineSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    width: '100%'
} as const;

const roleChipSx = {
    borderRadius: 999,
    height: 24,
    bgcolor: 'action.hover',
    color: 'text.primary',
    fontWeight: 500,
    '& .MuiChip-label': {
        px: 1,
        typography: 'caption'
    }
} as const;

export default function WorkEntryItem<TSource>({
    row,
    selected,
    toggleEntry,
    showDragHandle = true,
    showCheckbox = true,
    showKeyIcon = true,
    keyEntryTooltip,
    marketplaceInnerItemPadding = false,
    rowActionsEnabled = false,
    onEdit,
    onRequestDelete
}: WorkEntryItemProps<TSource>) {
    const [isHover, setIsHover] = useState(false);
    const hasFooter = Boolean(row.yearRange || row.location);
    const roleChipTileWidth = useMemo(() => estimateRoleChipTileWidth(row.roleLabels), [row.roleLabels]);
    const keyIcon = <Icons.ComedyMask fontSize={SIZES.medium} sx={{ color: 'primary.main', flexShrink: 0 }} />;

    const showRowActions =
        rowActionsEnabled && typeof onEdit === 'function' && typeof onRequestDelete === 'function';

    const handleEdit = useCallback(() => {
        onEdit?.(row);
    }, [onEdit, row]);

    const handleDelete = useCallback(() => {
        onRequestDelete?.(row);
    }, [onRequestDelete, row]);

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: ENTITY_CONSTANTS.person,
                icon: Icons.Edit,
                onClick: handleEdit
            },
            {
                securityAction: SECURITY_ACTIONS.DELETE,
                entityName: ENTITY_CONSTANTS.person,
                icon: Icons.Delete,
                onClick: handleDelete
            }
        ],
        [handleDelete, handleEdit]
    );

    const actionsContainerSx = useMemo(() => {
        if (!showRowActions) return undefined;
        if (showCheckbox) {
            return { p: 0, top: 'auto', right: 8, bottom: 8, zIndex: 2 };
        }
        return { p: 0 };
    }, [showCheckbox, showRowActions]);

    return (
        <H
            sx={(theme) => ({
                position: 'relative',
                width: '100%',
                height: ENTRY_ITEM_HEIGHT_PX,
                minHeight: ENTRY_ITEM_HEIGHT_PX,
                boxSizing: 'border-box',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100'
                },
                ...(marketplaceInnerItemPadding ? { p: MARKETPLACE_INNER_ITEM_PADDING } : { p: 1.5 }),
                gap: 1,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                overflow: 'hidden'
            })}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <V sx={{ flex: 1, minWidth: 0, height: '100%', gap: 0.5 }}>
                <H sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <H sx={{ alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        {showDragHandle ? (
                            <Icons.DragIndicator
                                sx={{ color: 'text.secondary', pointerEvents: 'none', flexShrink: 0 }}
                            />
                        ) : null}
                        {showKeyIcon && selected ? (
                            keyEntryTooltip ? (
                                <Tooltip title={keyEntryTooltip}>{keyIcon}</Tooltip>
                            ) : (
                                keyIcon
                            )
                        ) : null}
                        <EllipsisText
                            text={row.primaryTitle}
                            sx={{
                                typography: 'subtitle1',
                                fontWeight: 'fontWeightMedium',
                                color: 'text.primary',
                                flex: 1,
                                minWidth: 0
                            }}
                        />
                    </H>
                    {showCheckbox ? (
                        <Checkbox
                            checked={selected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleEntry(row.id)}
                            sx={{ p: 0 }}
                            slotProps={{
                                input: {
                                    'aria-label': `Select ${row.primaryTitle || 'entry'}`
                                }
                            }}
                        />
                    ) : null}
                </H>
                {row.secondaryLine ? (
                    <Typography variant="body2" color="text.secondary" sx={clampedLineSx}>
                        {row.secondaryLine}
                    </Typography>
                ) : null}
                {row.roleLabels.length > 0 ? (
                    <TagsPreview
                        tags={row.roleLabels}
                        tileWidth={roleChipTileWidth}
                        chipSx={roleChipSx}
                        sx={{ width: '100%', minWidth: 0 }}
                    />
                ) : null}
                {hasFooter ? (
                    <H
                        sx={{
                            mt: 'auto',
                            gap: 1,
                            minWidth: 0,
                            width: '100%',
                            alignItems: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        {row.yearRange ? (
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {row.yearRange}
                            </Typography>
                        ) : null}
                        {row.location ? (
                            <EllipsisText
                                text={row.location}
                                sx={{
                                    typography: 'caption',
                                    color: 'text.secondary',
                                    flex: 1,
                                    minWidth: 0
                                }}
                            />
                        ) : null}
                    </H>
                ) : null}
            </V>
            {showRowActions ? (
                <ResponsiveActions
                    isActivated={rowActionsEnabled ? isHover : undefined}
                    showBorder
                    securedActions={securedActions}
                    containerSx={actionsContainerSx}
                />
            ) : null}
        </H>
    );
}
